import { useCallback, useState } from 'react';

// Persisted "recently used" color history for the editor's color menus.
// Each menu (text color / highlight / cell background) keeps its OWN history
// under a distinct storage key — the vocabularies don't overlap (text colors
// are saturated, highlight/cell bgs are light tints), so mixing them in one
// list would just be confusing.
//
// The list is capped + dedup'd, newest first. Picking a color that's already
// in the list re-promotes it to the front rather than creating a duplicate
// entry. localStorage failures (private mode, quota) silently fall back to an
// in-memory only history — the user still sees recents this session, they
// just won't survive a reload.

const MAX_RECENT = 6;

export function useRecentColors(storageKey: string) {
    const [recents, setRecents] = useState<string[]>(() => {
        if (typeof window === 'undefined') return [];
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (!raw) return [];
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr)) return [];
            return arr.filter((c): c is string => typeof c === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(c)).slice(0, MAX_RECENT);
        } catch {
            return [];
        }
    });

    const pushRecent = useCallback(
        (color: string | undefined | null) => {
            if (!color || typeof color !== 'string') return;
            const normalized = color.toLowerCase();
            // Skip non-hex inputs (e.g. 'transparent', named colors) — the
            // native color picker only emits hex, but defensive guard keeps
            // the stored shape consistent.
            if (!/^#[0-9a-f]{3,8}$/.test(normalized)) return;
            setRecents((prev) => {
                const filtered = prev.filter((c) => c.toLowerCase() !== normalized);
                const next = [normalized, ...filtered].slice(0, MAX_RECENT);
                try {
                    window.localStorage.setItem(storageKey, JSON.stringify(next));
                } catch {
                    /* private mode / quota — recents stay in-memory only */
                }
                return next;
            });
        },
        [storageKey],
    );

    return [recents, pushRecent] as const;
}
